import {useNotificationStore, useUserStore} from '@/stores'
import {jsonRpc, mediator} from '@communication'

export default class NetworkProcedure {
  constructor() {
    this.packetLoss = 0
    this.rtt = 0 // Round Trip Time (Latency)
    this.jitter = 0
    this.intervalId = null
    this.poorCount = 0
    this.goodCount = 0
    this.previousInbound = new Map() // inbound-rtp id -> its counters at the previous sample
    this.poorNetworkToastDuration = 5000 // ms the "poor network" warning stays on screen
    this.poorNetworkToastTimer = 0
  }

  install = () => {
    this.intervalId = setInterval(this.updateNetwork.bind(this), Number(import.meta.env.VITE_DURATION_OF_NETWORK_QUALITY_MONITORING))
  }

  uninstall = () => {
    clearInterval(this.intervalId)
    clearTimeout(this.poorNetworkToastTimer)
  }

  calculateNetworkQuality() {
    // Our own signaling socket is down. The network machine (toast) also goes offline when another party's socket
    // closes, which says nothing about this device's network
    if (jsonRpc.status === 'DISCONNECTED') {
      return 'offline'
    }
    const {
      VITE_PACKAGE_LOSS_GOOD,
      VITE_PACKAGE_LOSS_AVERAGE,
      VITE_PACKAGE_LOSS_POOR,
      VITE_JITTER_GOOD,
      VITE_JITTER_AVERAGE,
      VITE_JITTER_POOR,
      VITE_RTT_GOOD,
      VITE_RTT_AVERAGE,
      VITE_RTT_POOR,
    } = import.meta.env
    let score = 0
    let latency = this.rtt && !isNaN(this.rtt) ? this.rtt : 0
    let loss = this.packetLoss > 0 ? this.packetLoss : 0
    let jitter = this.jitter

    // 1. Packet loss (%)
    if (loss < Number(VITE_PACKAGE_LOSS_GOOD)) {
      score += 2
    } else if (loss < Number(VITE_PACKAGE_LOSS_AVERAGE)) {
      score += 1
    } else if (loss < Number(VITE_PACKAGE_LOSS_POOR)) {
      score -= 1
    } else {
      score -= 2
    }

    // 2. Jitter (ms)
    if (jitter < Number(VITE_JITTER_GOOD)) {
      score += 2
    } else if (jitter < Number(VITE_JITTER_AVERAGE)) {
      score += 1
    } else if (jitter < Number(VITE_JITTER_POOR)) {
      score -= 1
    } else {
      score -= 2
    }

    // 3. RTT (ms)
    if (latency < Number(VITE_RTT_GOOD)) {
      score += 2
    } else if (latency < Number(VITE_RTT_AVERAGE)) {
      score += 1
    } else if (latency < Number(VITE_RTT_POOR)) {
      score -= 1
    } else {
      score -= 2
    }

    if (score >= 6) {
      return 'good'
    }else if (score >= 3) {
      return 'average'
    }else {
      return 'poor'
    }
  }

  async getStats() {
    const stats = await mediator.connection?.strategy.getMeasureStats()
    if (!stats) {
      this.previousInbound.clear()
      return
    }
    // packetsLost and packetsReceived count from the start of the stream, so the loss of the last interval is the
    // difference to the previous sample, over all incoming streams. A stream seen for the first time, or one whose
    // counters went back (a new connection reusing the id), only sets the baseline
    let lost = 0
    let received = 0
    const current = new Map()
    stats.forEach(report => {
      // Measure latency (RTT) and packet loss rate
      if (report.type === 'inbound-rtp') {
        const counters = {packetsLost: report.packetsLost ?? 0, packetsReceived: report.packetsReceived ?? 0}
        const previous = this.previousInbound.get(report.id)
        current.set(report.id, counters)
        if (previous && counters.packetsReceived >= previous.packetsReceived) {
          lost += Math.max(counters.packetsLost - previous.packetsLost, 0)
          received += counters.packetsReceived - previous.packetsReceived
        }
        this.jitter = report.jitter
      } else if (report.type === 'remote-inbound-rtp') {
        this.rtt = report.roundTripTime
      }
    })
    this.previousInbound = current
    this.packetLoss = lost + received > 0 ? lost / (lost + received) : 0
  }

  notifyUser = () => {
    const notificationStore = useNotificationStore()
    notificationStore.showPoorNetworkToast({poorNetworkToast: true})
    clearTimeout(this.poorNetworkToastTimer)
    this.poorNetworkToastTimer = setTimeout(() => {
      notificationStore.showPoorNetworkToast({poorNetworkToast: false})
    }, this.poorNetworkToastDuration)
  }
  updateNetworkQuality = (quality) => {
    const userStore = useUserStore()
    userStore.setQualityNetwork(quality)
  }
  updateNetwork = async () => {
    await this.getStats()
    const quality = this.calculateNetworkQuality()
    this.updateNetworkQuality(quality)
    if (quality === 'good' || quality === 'average') {
      this.goodCount++
      this.poorCount = 0
    } else if (quality === 'poor') {
      this.poorCount++
    }
    if (this.goodCount >= 1 && this.poorCount === 3) {
      this.notifyUser(true)
      this.goodCount = 0
      this.poorCount = 0
    }
  }
}
